/*-----------------------------------------------------------------------------/
/ Custom Theme JS
/-----------------------------------------------------------------------------*/

(() => {
  const EMAIL_REGEX =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateEmail = (input) => {
    const errorEl = document.getElementById(
      input.getAttribute("aria-describedby")
    );

    if (!errorEl) return true;

    const value = input.value.trim();
    let message = "";

    if (!value) {
      message =
        input.dataset.emailRequired ||
        "Please enter your email address.";
    } else if (!EMAIL_REGEX.test(value)) {
      message =
        input.dataset.emailInvalid ||
        "Please enter a valid email address.";
    }

    const isValid = !message;

    input.classList.toggle("is-invalid", !isValid);
    input.classList.toggle("is-valid", isValid);
    input.setAttribute("aria-invalid", String(!isValid));

    errorEl.textContent = message;
    errorEl.hidden = isValid;

    return isValid;
  };

  const initCustomCombobox = (select) => {
    if (!select || select.dataset.comboboxInitialized) return;
    select.dataset.comboboxInitialized = 'true';

    const parent = select.closest('.selector-wrapper') || select.closest('.form-group') || select.parentElement;
    const id = select.id || 'select_' + Math.random().toString(36).substring(2, 9);
    select.id = id;

    const label = document.querySelector(`label[for="${id}"]`) || parent.querySelector('label');
    const labelText = label ? label.textContent.replace('*', '').trim() : (select.getAttribute('aria-label') || 'Select option');
    const labelId = label ? (label.id || (label.id = id + '_label')) : '';

    const options = Array.from(select.options);
    let selIdx = Math.max(0, select.selectedIndex), activeIdx = selIdx, isOpen = false;

    select.classList.add('visually-hidden-select');
    select.style.cssText = 'position: absolute !important; opacity: 0 !important; pointer-events: none !important; width: 1px !important; height: 1px !important; clip: rect(0,0,0,0) !important; overflow: hidden !important; margin: -1px !important; border: 0 !important;';
    select.tabIndex = -1;

    const origClasses = select.className.replace('visually-hidden-select', '').replace(/validate\[[^\]]+\]/g, '').trim();

    const wrapper = document.createElement('div');
    wrapper.className = 'custom-combobox-wrapper';
    wrapper.innerHTML = `
        <button type="button" class="${origClasses} custom-combobox-btn" 
            id="${id}_combobox" role="combobox" aria-expanded="false" aria-haspopup="listbox" 
            aria-controls="${id}_listbox" aria-required="${select.required || false}"
            ${labelId ? `aria-labelledby="${labelId}"` : `aria-label="${labelText}"`}>
            <span id="${id}_val" class="custom-combobox-value" ${selIdx === 0 && options[0]?.value === '' ? 'aria-hidden="true"' : ''}>${options[selIdx]?.text || ''}</span>
            <span class="custom-combobox-arrow" aria-hidden="true">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
        </button>
        <ul id="${id}_listbox" class="custom-combobox-listbox" role="listbox" ${labelId ? `aria-labelledby="${labelId}"` : `aria-label="${labelText}"`} tabindex="-1" hidden>
            ${options.map((opt, i) => `
                <li role="option" id="${id}_opt_${i}" data-index="${i}" 
                    aria-label="${opt.text}"
                    aria-selected="${i === selIdx}" class="custom-combobox-option${i === selIdx ? ' is-selected' : ''}">
                    <span aria-hidden="true">${opt.text}</span>
                </li>
            `).join('')}
        </ul>
    `;

    if (label) label.setAttribute('for', `${id}_combobox`);
    parent.insertBefore(wrapper, select.nextSibling);
    parent.querySelectorAll('.icon--chevron, .icon-chevron, .icon--down').forEach(icon => {
        icon.style.cssText = 'display: none !important;';
    });

    const btn = wrapper.querySelector('button');
    const valSpan = wrapper.querySelector('.custom-combobox-value');
    const listbox = wrapper.querySelector('ul');
    const items = Array.from(listbox.children);

    const setHighlight = (idx) => {
        items.forEach((el, i) => {
            const focused = i === idx;
            el.classList.toggle('is-focused', focused);
            if (focused) {
                btn.setAttribute('aria-activedescendant', el.id);
                const listboxTop = listbox.scrollTop;
                const listboxBottom = listboxTop + listbox.clientHeight;
                const elTop = el.offsetTop;
                const elBottom = elTop + el.offsetHeight;
                if (elTop < listboxTop) {
                    listbox.scrollTop = elTop;
                } else if (elBottom > listboxBottom) {
                    listbox.scrollTop = elBottom - listbox.clientHeight;
                }
            }
        });
    };

    const toggleListbox = (open) => {
        isOpen = open;
        listbox.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.classList.toggle('is-active', open);
        wrapper.classList.toggle('is-open', open);
        if (!open) {
            btn.removeAttribute('aria-activedescendant');
            setHighlight(-1);
        } else {
            activeIdx = selIdx;
            setHighlight(activeIdx);
        }
    };

    const commit = (idx) => {
        selIdx = idx;
        select.selectedIndex = idx;
        select.value = options[idx].value;
        valSpan.textContent = options[idx].text;

        if (idx === 0 && options[0]?.value === '') {
            valSpan.setAttribute('aria-hidden', 'true');
        } else {
            valSpan.removeAttribute('aria-hidden');
        }

        if (select.value !== '') {
            select.removeAttribute('aria-invalid');
            select.classList.remove('form-validation-error');
            btn.removeAttribute('aria-invalid');
            btn.classList.remove('form-validation-error');

            const line = select.closest('.form-line');
            if (line) {
                line.classList.remove('form-line-error');
                const errMsgs = line.querySelectorAll('.form-error-message');
                errMsgs.forEach(m => m.remove());
            }
        }

        items.forEach((el, i) => {
            const isSel = i === idx;
            el.setAttribute('aria-selected', isSel);
            el.classList.toggle('is-selected', isSel);
        });

        select.dispatchEvent(new Event('change', { bubbles: true }));
        select.dispatchEvent(new Event('input', { bubbles: true }));
        if (select.form) {
            select.form.dispatchEvent(new Event('change', { bubbles: true }));
        }

        toggleListbox(false);
        btn.focus({ preventScroll: true });
    };

    btn.addEventListener('click', (e) => { e.preventDefault(); toggleListbox(!isOpen); });

    items.forEach((item, i) => {
        item.addEventListener('click', (e) => { e.stopPropagation(); commit(i); });
    });

    btn.addEventListener('keydown', (e) => {
        const max = options.length - 1;
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) toggleListbox(true);
                else setHighlight(activeIdx = Math.min(max, activeIdx + 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (!isOpen) toggleListbox(true);
                else setHighlight(activeIdx = Math.max(0, activeIdx - 1));
                break;
            case 'Home':
                if (isOpen) { e.preventDefault(); setHighlight(activeIdx = 0); }
                break;
            case 'End':
                if (isOpen) { e.preventDefault(); setHighlight(activeIdx = max); }
                break;
            case 'Enter': case ' ':
                e.preventDefault();
                if (isOpen) commit(activeIdx);
                else toggleListbox(true);
                break;
            case 'Escape':
                if (isOpen) { e.preventDefault(); e.stopPropagation(); toggleListbox(false); btn.focus(); }
                break;
            case 'Tab':
                if (isOpen) toggleListbox(false);
                break;
        }
    });

    document.addEventListener('click', (e) => { if (isOpen && !wrapper.contains(e.target)) toggleListbox(false); });

    select.addEventListener('invalid', (e) => {
        e.preventDefault();
        btn.setAttribute('aria-invalid', 'true');
        btn.classList.add('form-validation-error');
        btn.focus({ preventScroll: false });

        const line = select.closest('.form-line');
        if (line) {
            line.classList.add('form-line-error');
        }
    });

  };

  const init = () => {
    document
      .querySelectorAll(".newsletter__form-form")
      .forEach((form) => {
        form.setAttribute("novalidate", "");

        const emailInput = form.querySelector(
          'input[name="contact[email]"]'
        );

        if (!emailInput) return;

        emailInput.addEventListener("blur", () =>
          validateEmail(emailInput)
        );

        emailInput.addEventListener("input", () => {
          if (emailInput.classList.contains("is-invalid")) {
            validateEmail(emailInput);
          }
        });

        form.addEventListener(
          "submit",
          (event) => {
            if (!validateEmail(emailInput)) {
              event.preventDefault();
              event.stopImmediatePropagation();
              emailInput.focus();
            }
          },
          true
        );
      });

    const initAllComboboxes = () => {
      document.querySelectorAll(".js-collection-sort, .form-dropdown").forEach(initCustomCombobox);
    };

    // Target Android TalkBack in default linear navigation mode to announce Heading level 2
    const updateTalkbackAccordionHeadingLabels = () => {
      const isAndroid = /Android/i.test(navigator.userAgent);
      if (!isAndroid) return;

      document.querySelectorAll('.faq__accordion-heading .faq__accordion-button, .product-single__accordion-heading .product-single__accordion-button, .faq__accordion-heading button, [class*="accordion-heading"] button').forEach((btn) => {
        const parentHeading = btn.closest('h1, h2, h3, h4, h5, h6, [role="heading"]');
        let level = '2';
        if (parentHeading) {
          const tagName = parentHeading.tagName.toLowerCase();
          if (tagName.startsWith('h') && tagName.length === 2) {
            level = tagName.substring(1);
          } else if (parentHeading.getAttribute('aria-level')) {
            level = parentHeading.getAttribute('aria-level');
          }
        }
        const titleEl = btn.querySelector('.faq__accordion-title, .pdp-accordion-title') || btn;
        const titleText = titleEl.textContent ? titleEl.textContent.trim() : '';
        if (titleText && (!btn.hasAttribute('aria-label') || !btn.getAttribute('aria-label').includes('Heading level'))) {
          btn.setAttribute('aria-label', `${titleText}, Heading level ${level}`);
        }
      });
    };

    initAllComboboxes();
    updateTalkbackAccordionHeadingLabels();



    // Accordion Toggle Handler for PDP & FAQ accordions (heading / button clicks)
    document.addEventListener("click", (e) => {
      const headingOrBtn = e.target.closest(".faq__accordion-button, .product-single__accordion-button, .faq__accordion-heading, .product-single__accordion-heading");
      if (!headingOrBtn) return;

      const item = headingOrBtn.closest(".faq__accordion-item, .product-single__accordion-item");
      if (!item) return;

      const group = item.closest("accordion-group");
      if (group && group.accordions && group.accordions[item.id]) {
        return;
      }

      e.preventDefault();
      const btn = item.querySelector(".faq__accordion-button, .product-single__accordion-button") || headingOrBtn;
      const panelId = btn.getAttribute ? btn.getAttribute("aria-controls") : null;
      const panel = (panelId ? document.getElementById(panelId) : null) || item.querySelector(".faq__accordion-panel, .product-single__accordion-panel, [role='region'], .product-single__accordion__item-wrap");
      if (!panel) return;

      const isExpanded = btn.getAttribute ? btn.getAttribute("aria-expanded") === "true" : item.classList.contains("is-open");

      if (isExpanded) {
        if (btn.setAttribute) btn.setAttribute("aria-expanded", "false");
        item.classList.remove("is-open");
        panel.style.overflow = "hidden";
        const computedStyle = window.getComputedStyle(panel);
        const currentPaddingTop = computedStyle.paddingTop;
        const currentPaddingBottom = computedStyle.paddingBottom;

        const anim = panel.animate({
          height: [`${panel.offsetHeight}px`, "0px"],
          paddingTop: [currentPaddingTop, "0px"],
          paddingBottom: [currentPaddingBottom, "0px"],
          opacity: [1, 0]
        }, { duration: 300, easing: "cubic-bezier(0.25, 1, 0.5, 1)" });

        anim.onfinish = () => {
          panel.setAttribute("hidden", "");
          panel.style.display = "none";
          panel.style.height = "";
          panel.style.paddingTop = "";
          panel.style.paddingBottom = "";
          panel.style.overflow = "";
          panel.style.opacity = "";
        };
      } else {
        if (btn.setAttribute) btn.setAttribute("aria-expanded", "true");
        item.classList.add("is-open");
        panel.removeAttribute("hidden");
        panel.style.display = "block";
        panel.style.overflow = "hidden";

        const computedStyle = window.getComputedStyle(panel);
        const targetPaddingTop = computedStyle.paddingTop;
        const targetPaddingBottom = computedStyle.paddingBottom;

        // Force synchronous layout reflow so scrollHeight is > 0
        void panel.offsetHeight;
        const targetHeight = panel.scrollHeight || panel.offsetHeight;

        const anim = panel.animate({
          height: ["0px", `${targetHeight}px`],
          paddingTop: ["0px", targetPaddingTop],
          paddingBottom: ["0px", targetPaddingBottom],
          opacity: [0, 1]
        }, { duration: 350, easing: "cubic-bezier(0.25, 1, 0.5, 1)" });

        anim.onfinish = () => {
          panel.style.height = "";
          panel.style.paddingTop = "";
          panel.style.paddingBottom = "";
          panel.style.overflow = "";
          panel.style.opacity = "";
        };
      }
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();